import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, formatApiErrorDetail } from "@/lib/api";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Receipt, CheckCircle, Warning, FileText } from "@phosphor-icons/react";
import { PoweredByStrip } from "@/pages/LandlordOnboardPublic";

export default function DepositSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [signName, setSignName] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [tab, setTab] = useState("sign");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "signed" | "disputed"

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/deposits/${token}`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.status === 404 ? "This link is invalid or expired." : "Could not load your reconciliation.");
    }
  };
  useEffect(() => { load(); }, [token]);

  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  const sign = async () => {
    if (!signName.trim()) return toast.error("Please type your full name to sign");
    setBusy(true);
    try {
      await axios.post(`${API}/public/deposits/${token}/sign`, { signed_by_name: signName.trim() });
      setDone("signed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Sign failed")); }
    finally { setBusy(false); }
  };
  const dispute = async () => {
    if (!disputeReason.trim() || disputeReason.trim().length < 10) return toast.error("Please explain the dispute (10+ characters)");
    setBusy(true);
    try {
      await axios.post(`${API}/public/deposits/${token}/dispute`, { reason: disputeReason.trim() });
      setDone("disputed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dispute failed")); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white p-6 border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] text-center">
        <Warning size={48} className="text-red-500 mx-auto mb-3"/>
        <div className="font-black uppercase tracking-widest text-lg">{err}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center font-black">Loading…</div>;

  const brand = data.tenant_brand_color || "#0f172a";
  const isDone = data.status === "signed" || data.status === "disputed" || done;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="deposit-sign-page">
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-4">
        <div className="p-6 text-white shadow-[6px_6px_0_#0f172a]" style={{ background: brand }}>
          {data.tenant_has_logo && (
            <img src={`${API}/tenant/logo/${data.tenant_id}`} alt={data.tenant_brand_name}
              className="max-h-14 max-w-[220px] mb-3 bg-white/10 p-1"
              onError={e => { e.currentTarget.style.display = "none"; }}/>
          )}
          <div className="text-[10px] font-black uppercase tracking-widest opacity-70">
            {data.tenant_brand_name}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Receipt weight="fill" size={28}/>
            <h1 className="text-2xl md:text-3xl font-black uppercase tracking-widest">Deposit Reconciliation</h1>
          </div>
          <div className="text-sm mt-2 opacity-90">
            {data.property_address} {data.unit && `· ${data.unit}`}
          </div>
          <div className="text-xs mt-1 opacity-80">
            {data.tenant_name} · Moved out {data.move_out_date || "—"}
          </div>
          {(data.tenant_contact_email || data.tenant_contact_phone) && (
            <div className="text-[10px] mt-2 opacity-80 flex flex-wrap gap-3">
              {data.tenant_contact_email && (
                <a href={`mailto:${data.tenant_contact_email}`} className="underline">
                  Questions? {data.tenant_contact_email}
                </a>
              )}
              {data.tenant_contact_phone && (
                <a href={`tel:${data.tenant_contact_phone}`} className="underline">
                  {data.tenant_contact_phone}
                </a>
              )}
            </div>
          )}
        </div>

        <PoweredByStrip tenantName={data.tenant_brand_name}/>

        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 bg-white border-4 border-slate-900 shadow-[3px_3px_0_#0f172a]" data-testid="held-amount">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Held</div>
            <div className="text-2xl md:text-3xl font-black">{money(data.security_deposit_cents)}</div>
          </div>
          <div className="p-4 bg-red-50 border-4 border-red-600 shadow-[3px_3px_0_#0f172a]" data-testid="deductions-amount">
            <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Deductions</div>
            <div className="text-2xl md:text-3xl font-black text-red-700">− {money(data.deductions_total_cents)}</div>
          </div>
          <div className={`p-4 border-4 shadow-[3px_3px_0_#0f172a] ${
            data.refund_cents > 0 ? "bg-emerald-50 border-emerald-600" : "bg-amber-50 border-amber-600"
          }`} data-testid="refund-amount">
            <div className="text-[10px] font-black uppercase tracking-widest">
              {data.refund_cents > 0 ? "Refund to You" : "Owed by You"}
            </div>
            <div className={`text-2xl md:text-3xl font-black ${
              data.refund_cents > 0 ? "text-emerald-700" : "text-amber-700"
            }`}>
              {money(data.refund_cents > 0 ? data.refund_cents : data.owed_by_tenant_cents)}
            </div>
          </div>
        </div>

        <div className="bg-white p-4 border-4 border-slate-900 shadow-[3px_3px_0_#0f172a]">
          <h2 className="font-black uppercase tracking-widest text-sm mb-2">Itemized Deductions</h2>
          {(data.deductions || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">No deductions — full refund.</div>
          ) : (
            <div className="space-y-1">
              {data.deductions.map((d, i) => (
                <div key={i} className="grid grid-cols-6 gap-2 p-2 border-b border-slate-100 items-start" data-testid={`ded-item-${i}`}>
                  <div className="col-span-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    {(d.category || "").replace("_", " ")}
                  </div>
                  <div className="col-span-4 text-sm">
                    <div className="font-bold">{d.description}</div>
                    {d.notes && <div className="text-[11px] text-slate-500">{d.notes}</div>}
                  </div>
                  <div className="col-span-1 text-right text-sm font-black text-red-700">
                    − {money(d.amount_cents)}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3">
            <a href={`${API}/public/deposits/${token}/pdf`} target="_blank" rel="noopener noreferrer"
              data-testid="download-pdf"
              className="inline-flex items-center gap-1 h-9 px-3 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px]">
              <FileText size={12}/> Download Full PDF (Condition Reports Included)
            </a>
          </div>
        </div>

        {isDone ? (
          <div className="bg-white p-6 border-4 border-emerald-600 shadow-[3px_3px_0_#0f172a] text-center" data-testid="done-panel">
            <CheckCircle size={48} weight="fill" className={`mx-auto mb-2 ${
              (done || data.status) === "disputed" ? "text-amber-600" : "text-emerald-600"
            }`}/>
            <div className="font-black uppercase tracking-widest text-lg">
              {(done || data.status) === "disputed" ? "Dispute recorded" : "Signed — thank you"}
            </div>
            <div className="text-sm text-slate-600 mt-1">
              {(done || data.status) === "disputed"
                ? "Our office will review your dispute and follow up within 3 business days."
                : `Signed as ${data.signed_by_name || signName} — a copy has been saved for your records.`}
            </div>
          </div>
        ) : (
          <div className="bg-white p-4 border-4 border-slate-900 shadow-[3px_3px_0_#0f172a]">
            <div className="flex gap-1 mb-3">
              <button onClick={() => setTab("sign")} data-testid="tab-sign"
                className={`h-10 px-4 font-black uppercase tracking-widest text-[11px] border-2 border-slate-900 ${
                  tab === "sign" ? "bg-emerald-600 text-white" : "bg-white hover:bg-slate-100"
                }`}>Accept & Sign</button>
              <button onClick={() => setTab("dispute")} data-testid="tab-dispute"
                className={`h-10 px-4 font-black uppercase tracking-widest text-[11px] border-2 border-slate-900 ${
                  tab === "dispute" ? "bg-red-600 text-white" : "bg-white hover:bg-slate-100"
                }`}>Dispute</button>
            </div>
            {tab === "sign" ? (
              <div className="space-y-3">
                <div className="text-sm text-slate-700">
                  By typing your name and clicking Sign, you acknowledge the itemized deductions
                  above and agree to the final refund/owed amount.
                </div>
                <input value={signName} onChange={e => setSignName(e.target.value)}
                  placeholder="Type your full name here"
                  data-testid="sign-name-input"
                  className="w-full h-12 px-4 border-2 border-slate-900 text-lg font-serif italic"/>
                <button onClick={sign} disabled={busy || !signName.trim()}
                  data-testid="sign-submit"
                  className="w-full h-12 bg-emerald-600 text-white font-black uppercase tracking-widest disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
                  {busy ? "Signing…" : "Sign Reconciliation"}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-slate-700">
                  Please explain what you disagree with. Include specifics (which item, why, any evidence).
                  We'll review and respond within 3 business days.
                </div>
                <textarea value={disputeReason} onChange={e => setDisputeReason(e.target.value)}
                  rows={5} placeholder="I disagree with…" data-testid="dispute-reason"
                  className="w-full p-3 border-2 border-slate-900 text-sm"/>
                <button onClick={dispute} disabled={busy || disputeReason.trim().length < 10}
                  data-testid="dispute-submit"
                  className="w-full h-12 bg-red-600 text-white font-black uppercase tracking-widest disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
                  {busy ? "Submitting…" : "Submit Dispute"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
